import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  FetchVatDefinitionListParams,
  VATDefinition
} from 'Services/ProductManagement/Features/VATDefinition/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface VATDefinitionListProps {
  rows: VATDefinition[]
  isLoading: boolean
  params: FetchVatDefinitionListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string) => void
  selectedVatDefinitionId: string | undefined
}

export default function VATDefinitionList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedVatDefinitionId
}: Readonly<VATDefinitionListProps>) {
  const headCells = [
    {
      id: 'title',
      label: t`Title`,
      numeric: false,
      sortable: true,
      disablePadding: false
    },
    {
      id: 'vat_percentages',
      label: t`VAT percentages`,
      numeric: false,
      sortable: false,
      disablePadding: false
    },
    {
      id: 'created_timestamp',
      label: t`Created`,
      numeric: false,
      sortable: true,
      disablePadding: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  const vatPercentagesToDisplay = (vatDefinition: VATDefinition) => {
    const percentages = vatDefinition.vat_percentages.map(
      vatPercentage => `${vatPercentage.vat_code} (${vatPercentage.vat_percent} %)`
    )
    return percentages.length > 0 ? percentages.join(', ') : '-'
  }

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows?.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.title}
                    selected={selectedVatDefinitionId === row.id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {showValueOrHyphen(row.title)}
                    </ClickableTableCell>
                    <NoWrapTableCell>{vatPercentagesToDisplay(row)}</NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(row.created_timestamp)}>
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
